import React, { useState } from 'react';
import { View } from 'react-native';

import Item from './components/item';
import Loading, { SmallLoading } from '@components/loading';
import Refresh from '@components/common/Refresh';
import Small from './components/small';

import { isPhone, calculateTableConfig, NC_FILE_LEVELS, deepClone } from '@helpers';
import { scale, fullWidth } from '@components/frame';
import { TABLE_NAMES, TILE_CONFIG, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { LevelSubScreen } from '@v2/pages/common/enums';
import DataGrid from '@components/common/DataGrid';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { getNCFileDataGridColumns } from './columns';
import { handleOpen } from './helpers';

export default (props) => {
  const {
    totalSearchResult,
    searchResult,
    isError,
    loaded,
    C,
    filter,
    isSmallMode,
    isLineMode,
    fetchData,
    generateImageURL,
    machineSearch,
    isTableMode,
    attachMode,
    attachImageId,
    updateImageMetadata,
    onItemSelect,
    isPopupMode,
    popupWidth,
  } = props;

  const { IS_NARROW_WEB } = useDeviceSize();
  const [selectedItem, setSelectedItem] = useState(null);
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const effectiveWidth = isPopupMode && popupWidth ? popupWidth : fullWidth;
  const config = {
    width: effectiveWidth,
    scale,
    initialBaseSize: isSmallMode ? TILE_CONFIG.BASE_WIDTH_SMALL : TILE_CONFIG.BASE_WIDTH,
    spaceBetween: isSmallMode ? TILE_CONFIG.SPACE_BETWEEN_SMALL : TILE_CONFIG.SPACE_BETWEEN_NORMAL,
  };
  const { columns, itemSize } = calculateTableConfig(config);

  const open = (item, levelSubScreen = LevelSubScreen.GENERAL) => {
    handleOpen({
      item,
      levelSubScreen,
      isPopupMode,
      onItemSelect,
      attachMode,
      attachImageId,
      updateImageMetadata,
      navigateToMainDetailsScreen,
    });
  };

  const data = deepClone(searchResult);

  const isLinked = filter === NC_FILE_LEVELS.Productive || filter === NC_FILE_LEVELS.All;
  const isUnknown = filter === NC_FILE_LEVELS.Update;

  const additions = columns - (data.length % columns);
  if (additions !== columns && isLinked) {
    for (let i = 0; i < additions; i++) {
      data.push('empty');
    }
  }

  if (isTableMode) {
    const dataGridColumns = getNCFileDataGridColumns({ scale, goToDetails: open, attachMode });

    return (
      <View testID={TEST_IDS.searchScreen.ncFiles.dataGridContainer} style={{ width: effectiveWidth, height: '100%' }}>
        <DataGrid
          {...{
            ...props,
            columns: dataGridColumns,
            data,
            primaryKey: 'ncFileName',
            onRowClick: open,
            idName: 'ncFileId',
            tableName: props.tableName || TABLE_NAMES.ncFile,
          }}
        />
      </View>
    );
  }

  if (isError) {
    return <Refresh onPress={fetchData} />;
  }

  if (!loaded) {
    return <Loading />;
  }

  const renderElement = (props) => {
    const { index } = props;

    if (isLineMode) {
      return <Small {...props} key={'Item' + index} />;
    }
    return <Item {...{ ...props, isSelected: props.item.ncFileId === selectedItem }} key={'Item' + index} />;
  };

  return (
    <View style={{ width: effectiveWidth, height: '100%' }}>
      <View
        testID={TEST_IDS.searchScreen.ncFiles.itemContainer}
        style={[
          {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: scale(IS_NARROW_WEB ? 11 : isPhone ? -7 : -5),
          },
          isLinked && !isLineMode
            ? {
                paddingLeft: scale(isPopupMode ? 20 : 18),
                paddingRight: scale(isPopupMode ? 20 : 18),
                justifyContent: 'space-between',
                flexDirection: 'row',
                flexWrap: 'wrap',
              }
            : {
                paddingBottom: scale(150),
              },
          isLineMode && { marginTop: 0 },
        ]}
      >
        {data.map((item, index) =>
          renderElement({
            index,
            itemSize,
            generateImageURL,
            isSmallMode,
            isLineMode,
            goToDetails: open, // Use open instead of goToDetails for attachment mode
            isEmpty: item === 'empty',
            C,
            item,
            isUnknown,
            isFirst: index === 0,
            isLast: index === data.length - 1,
            machineSearch,
            effectiveWidth,
            isPopupMode,
            attachMode,
            attachImageId,
          })
        )}
      </View>
      {isLinked && totalSearchResult !== searchResult.length && (
        <View
          style={{
            marginTop: scale(20),
            backgroundColor: C('Background / Light / System'),
            justifyContent: 'center',
          }}
        >
          <SmallLoading />
        </View>
      )}
    </View>
  );
};
